import dayjs from 'dayjs'

import { VisitorInsightRow } from '@/common/api/params/customer'

import AiButton from '@/components/view/atoms/button/AiButton'
import AiDots from '@/components/view/atoms/loading/AiDots'
import Text, { TextColor } from '@/components/view/atoms/text/Text'

import {
  VISITOR_INSIGHT_ETA_SEC,
  VISITOR_INSIGHT_STEPS,
  VisitorInsightState
} from '@/components/container/modules/recommend/customer/summary/useVisitorInsight'

/**
 * 訪問者サマリのAI示唆（2026-10-04 追加・SMART_HUB_DEV-127）。
 *
 * 材料はサマリの集計値だけ。出力の数値はサーバで集計値と突き合わせ、
 * 合わないものは「注意」として下に出す。
 */

const QUESTION_LABEL: Record<string, string> = {
  funnel: '落ちている段階',
  segment: '区分の違い',
  caution: '言えないこと'
}

/** 実行中の進捗。無言だと固まったように見えるので、段階・スピナー・残り時間の目安を必ず出す */
const Progress = ({ state }: { state: VisitorInsightState }) => {
  if (!state.isMutating) return null
  const remain = Math.max(0, VISITOR_INSIGHT_ETA_SEC - state.elapsed)
  return (
    <div className='flex flex-wrap items-center gap-2' role='status' aria-live='polite'>
      <AiDots />
      <Text size={12} color={TextColor.SECONDARY}>
        {state.step}/{VISITOR_INSIGHT_STEPS.length}:{' '}
        {VISITOR_INSIGHT_STEPS[Math.max(0, state.step - 1)]}…
        {remain > 0 ? `（残り約${remain}秒）` : '（もう少しかかっています）'}
      </Text>
    </div>
  )
}

const InsightBody = ({ row }: { row: VisitorInsightRow }) => {
  const o = row.output
  return (
    <div className='flex flex-col gap-4'>
      <Text size={10} color={TextColor.TERTIARY}>
        第{row.generation}世代 ・{' '}
        {row.created_at ? dayjs(row.created_at).format('YYYY/MM/DD HH:mm') : ''}
        {row.operator_name ? ` ・ ${row.operator_name}` : ''}
        {` ・ 直近${row.days}日`}
      </Text>

      {o.summary && <Text size={14}>{o.summary}</Text>}

      {o.findings.map((f, i) => (
        <div key={`${f.question}-${i}`} className='flex flex-col gap-1'>
          <div className='flex flex-wrap items-baseline gap-2'>
            <Text size={10} color={TextColor.LINK} weight='bold' nowrap>
              {QUESTION_LABEL[f.question ?? ''] ?? f.question}
            </Text>
            <Text size={13} weight='bold'>
              {f.title}
            </Text>
          </div>
          <Text size={13} color={TextColor.SECONDARY}>
            {f.body}
          </Text>
        </div>
      ))}

      {o.not_covered.length > 0 && (
        <div className='flex flex-col gap-1'>
          <Text size={12} weight='bold'>
            このデータでは触れなかったこと
          </Text>
          {o.not_covered.map((x, i) => (
            <Text key={i} size={12} color={TextColor.SECONDARY}>
              ・{x}
            </Text>
          ))}
        </div>
      )}

      {row.warnings.length > 0 && (
        <div className='border-border-input flex flex-col gap-1 border-t pt-3'>
          <Text size={12} weight='bold' color={TextColor.CAUTION}>
            この示唆についての注意
          </Text>
          {row.warnings.map((w, i) => (
            <Text key={i} size={12} color={TextColor.SECONDARY}>
              ・{w.message}
            </Text>
          ))}
        </div>
      )}
    </div>
  )
}

const VisitorInsightSection = ({ state }: { state: VisitorInsightState }) => {
  return (
    <div className='flex flex-col gap-3'>
      <div className='flex flex-wrap items-center gap-3'>
        <AiButton loading={state.isMutating} disabled={state.isMutating} onClick={state.run}>
          AIで傾向を読む
        </AiButton>
        <Text size={10} color={TextColor.TERTIARY}>
          上の数字だけを材料にします。区分の差を原因とは断定しません。
          {state.keepGenerations}世代まで残ります。
        </Text>
      </div>

      <Progress state={state} />

      {state.error && (
        <Text size={12} color={TextColor.CRITICAL}>
          {state.error}
        </Text>
      )}

      {state.items.length > 1 && (
        <div className='flex flex-wrap items-center gap-2'>
          <Text size={12} color={TextColor.SECONDARY}>
            履歴
          </Text>
          {state.items.map((x) => (
            <button
              key={x.id}
              type='button'
              className={`text-size-12 rounded-lg border px-3 py-1 ${
                x.id === state.current?.id ? 'border-border-input bg-input font-bold' : 'border-transparent'
              }`}
              onClick={() => state.setSelected(x.id)}
            >
              第{x.generation}世代
            </button>
          ))}
        </div>
      )}

      {state.current && <InsightBody row={state.current} />}
    </div>
  )
}

export default VisitorInsightSection
